import { omit } from 'lodash-es';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DeliveryFeesStripeVariations } from 'state/launchdarkly/variations';
import { platform } from 'utils/environment';

import { IDeliveryFeesStripe, IDeliveryFeesTier } from './types';

const calculateFees = (tier: IDeliveryFeesTier) => {
  return Object.values(omit(tier, 'serviceFeePercent')).reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );
};

export const useDeliveryFeesStripe = (): IDeliveryFeesStripe | null => {
  const deliveryFeesTiers = useFlag(LaunchDarklyFlag.TIERED_DELIVERY_FEES);
  const deliveryFeesStripe = useFlag(LaunchDarklyFlag.DELIVERY_FEES_STRIPE);
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  if (!deliveryFeesStripe || deliveryFeesStripe === DeliveryFeesStripeVariations.DISABLED) {
    return null;
  }
  const isTitleOnly = deliveryFeesStripe === DeliveryFeesStripeVariations.ENABLED_WITH_TITLE_ONLY;
  const tiers = Object.keys(deliveryFeesTiers);

  // Find smallCartFeeThreshold
  const smallCartFeeThresholdAmount =
    tiers.find(key => {
      const platformTier = deliveryFeesTiers[key]?.[platform()];
      return platformTier && platformTier.smallCartFee === 0;
    }) || 0;

  const values = {
    minimum: checkoutDeliveryPriceMinimum,
    // The calculated smallCartFeeThreshold
    smallCartFeeThreshold: Number(smallCartFeeThresholdAmount),
    // The current tier used by the modal is only presented when the flag variation is ENABLED_WITH_TITLE_ONLY
    current: isTitleOnly && Object.values(deliveryFeesTiers)[0][platform()],
    // The tiers are only presented when the flag variation is ENABLED_WITH_TITLE_ONLY
    tiers: isTitleOnly
      ? []
      : Object.values(deliveryFeesTiers).map((tier, index) => ({
          tier: tiers?.[index + 1] ? Number(tiers[index + 1]) : Number(tiers[index]),
          fees: calculateFees(tier[platform()]),
        })),
  };
  return values;
};
